import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ num, title, children }: { num: string; title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 7 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(255,159,67,0.08)', border: '1px solid rgba(255,159,67,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 16, color: '#ff9f43' }}>{num}</Typography>
        </Box>
        <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.3rem', md: '1.7rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', pt: 0.5 }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

export default function BlogEmotionalAbusePage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Emotional Abuse in Daycare — What AI Can Hear That Cameras Miss" description="Shouting, threats, and verbal humiliation leave no visible trace. How audio AI detects emotional abuse in childcare facilities in real time." path="/blog/emotional-abuse-detection" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Emotional Safety</Typography>
        </Box>

        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(255,159,67,0.08)', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#ff9f43', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Emotional Safety · 6 min read</Typography>
        </Box>
        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          The Abuse Cameras Can't See: How AI Detects Emotional Abuse in Daycares
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          Shouting, humiliation, verbal threats — emotional abuse leaves no bruises but causes lasting harm. Here's how audio AI detection changes what's possible.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Para>When we talk about daycare abuse, we almost always mean physical abuse — the bruises, the broken bones, the footage that eventually surfaces. But there's another category of harm that is just as damaging and far harder to detect: emotional abuse.</Para>
        <Para>A worker who yells at toddlers, humiliates children who cry, threatens punishment, or uses degrading language every shift — this person leaves no physical evidence. Until now, they were essentially invisible to any monitoring system.</Para>

        <Section num="1" title="What is emotional abuse in childcare?">
          <Para>Emotional abuse in daycare settings includes any sustained pattern of behavior that harms a child's psychological wellbeing. It is not a single outburst — it's a pattern.</Para>
          <Stack spacing={1.5} sx={{ mb: 3, pl: 1 }}>
            {[
              'Verbal threats: "If you cry again, something bad will happen."',
              "Humiliation: mocking a child's appearance, abilities, or needs in front of others",
              'Degrading language: name-calling, contemptuous or dismissive tone directed at children',
              'Shouting as control: raised voices used routinely to intimidate rather than manage',
              'Isolation as punishment: removing a child from peers as a behavioral response',
              "Emotional neglect: deliberate ignoring of a child's distress signals",
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ color: '#ff9f43', fontWeight: 700, fontSize: 16, flexShrink: 0, lineHeight: 1.5 }}>›</Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
              </Box>
            ))}
          </Stack>
          <Para>It leaves no bruises. Children under 4 often cannot articulate what is happening to them. And without audio detection, it is essentially invisible to any safety system.</Para>
        </Section>

        <Section num="2" title="Why emotional abuse is chronically underreported">
          <Para>Physical abuse gets reported because it leaves evidence — a bruise, a fracture, a captured video. Emotional abuse has none of these triggers. It persists for months or years precisely because no system catches it.</Para>
          <Stack spacing={1} sx={{ mb: 3, pl: 1 }}>
            {[
              'Children too young to report — many victims are pre-verbal',
              'No physical evidence — injuries cannot be observed or photographed',
              'Staff loyalty culture — colleagues may minimize or normalize what they witness',
              'No audio recording in almost all facilities — the behavior happens without a trace',
              'Patterns only visible over time — a single incident looks like stress, not abuse',
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9499b0', mt: 0.7, flexShrink: 0 }} />
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
              </Box>
            ))}
          </Stack>
          <Para>The result: emotional abusers in childcare settings are rarely caught. They move between facilities. They accumulate victims across years. And without audio AI, they remain invisible.</Para>
        </Section>

        <Section num="3" title="What SMCLA's audio AI detects">
          <Para>SMCLA's audio analysis is trained specifically on childcare worker behavior. It doesn't just detect loud sounds — it looks for behavioral patterns that deviate from both the facility baseline and the individual worker's own baseline.</Para>
          <Box sx={{ p: 3, bgcolor: 'rgba(255,159,67,0.04)', border: '1px solid rgba(255,159,67,0.15)', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#ff9f43', mb: 1 }}>What the AI listens for:</Typography>
            <Stack spacing={1}>
              {[
                'Raised voice frequency and intensity — volume anomalies outside normal childcare ranges',
                'Verbal patterns directed at a specific child over multiple sessions',
                'Tone deviation from a worker\'s own communication baseline',
                'Distress vocalizations from children — crying, screaming, sounds of distress',
                'Repeated verbal patterns (same words, same tone, same child) across days and weeks',
                'Isolation behavior correlated with voice pattern changes — audio + visual combined',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#ff9f43', mt: 0.7, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          <Para>Critically, SMCLA combines audio and video signals. A worker who lowers their voice while physically isolating a child is a different behavioral signature than one who raises their voice in an open room. The combination catches what each signal alone would miss.</Para>
        </Section>

        <Section num="4" title="A real case: Centennial, Colorado (2025)">
          <Para>Brandon Vigil, 32, a preschool teacher in Centennial, Colorado, was charged with 51 criminal counts of child abuse and harassment against 2-year-olds. Investigators documented "many instances of harmful, degrading, and humiliating actions."</Para>
          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65, fontStyle: 'italic' }}>
              "These weren't only bruises — they were words and acts of humiliation, carried out over weeks against the same children. Audio detection would have flagged the vocal pattern within the first week."
            </Typography>
          </Box>
          <Para>51 counts. The pattern existed from the first incident. Without audio analysis, the behavior was invisible until the count was catastrophic. With SMCLA, a repeated degrading vocal pattern toward the same group of toddlers would have triggered an alert within days — not after 51 documented acts.</Para>
          <Para>This is the operational definition of emotional abuse detection: catching the pattern before the harm compounds.</Para>
        </Section>

        <Section num="5" title="Audio compliance — what you need to know">
          <Para>Audio recording in childcare settings is legal in all US states, but some states — notably California — require all-party consent for audio recording. This is not a barrier; it's a setup step.</Para>
          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 1 }}>How SMCLA handles compliance:</Typography>
            <Stack spacing={1}>
              {[
                'We assess your state\'s consent requirements during onboarding',
                'We provide ready-made consent disclosure language for staff agreements',
                'We advise on facility signage requirements where applicable',
                'Audio is processed for behavioral analysis — not stored as a raw continuous recording',
                'Parent notification language is available if required by your jurisdiction',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ color: '#5ce1e6', fontWeight: 700, fontSize: 16, flexShrink: 0, lineHeight: 1.5 }}>›</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          <Para>Every facility we deploy in completes a compliance checklist before audio analysis goes live. You are protected legally before the system runs.</Para>
        </Section>

        <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 6 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 1.5 }}>The bottom line</Typography>
          <Para>Cameras catch what happens when someone is watching. SMCLA catches what happens every day — including the words that leave no bruises but cause the most lasting harm.</Para>
          <Para>No other system on the market detects emotional abuse. This is not a feature gap — it's a category gap. SMCLA is the only platform that closes it.</Para>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(255,159,67,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            Hear what your cameras can't
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            Book a 30-minute demo to see how SMCLA's audio + visual AI works in your specific facility — including how we handle consent compliance for your state.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#ff9f43', color: '#0a0a12', borderRadius: 2, '&:hover': { bgcolor: '#e8903a' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
